import { useTranslation } from "react-i18next";
import { shallowEqual } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import UserSettingsService from "src/services/userSettingsService";
import { useDispatch, useSelector } from "src/store";

import { applyDevicePageFiltersExternally, closeDetails, updateSearchQuery } from "@dashboard/devices/store";
import { setColumnVisibility } from "@dashboard/devices/utils/columnVisibility";
import GroupSelectors, { selectGroupChildrenIds, selectGroups } from "@dashboard/groups/store/selectors";
import { getNestedGroupsWithIndentation, validateGroupForm } from "@dashboard/groups/utils";
import { useCurrentWorkspace, useWorkspaceFeature } from "@dashboard/workspaces/api/helpers";
import { showDetails, updateGroup, updateGroupParent } from "@devices/store";
import { Group } from "@devices/types";
import LicenseSelectors from "@licenses/store/selectors";
import { PermissionService } from "@services/permissionService/permissionService";
import { TextLink } from "@shared/TextLink";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { PaneElement } from "@sidePanel/shared/PaneElement";
import { PropertyKey, PropertyRow, PropertyValue } from "@sidePanel/shared/Property";

const OverviewTab = (props: Group) => {
    const { t } = useTranslation(["common", "sidepanels"]);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const isOnDevicePage = location.pathname === "/dashboard/devices";

    // The links open the device table filtered to this group alone, so the counts leave out its subgroups.
    const onlineDeviceCount = props.statistics?.own.connectionStatus.online;
    const offlineDeviceCount = props.statistics?.own.connectionStatus.offline;

    const totalLicenses = useSelector((state) => LicenseSelectors.selectLicensesByGroupId(state, props.id), shallowEqual);

    const unusedLicenses = totalLicenses.filter((license) => !license.device);
    const unusedLicenseCount = unusedLicenses.length;
    const activeLicenseCount = totalLicenses.length - unusedLicenseCount;
    const parentGroup = useSelector((state) => (props.parentId ? GroupSelectors.selectGroupById(state, props.parentId) : null));

    const groups = useSelector(selectGroups);
    const groupNestingLimit = useWorkspaceFeature("GroupNestingLimit");
    const childrenGroupIds = useSelector((state) => selectGroupChildrenIds(state, props.id));

    const { data: workspace } = useCurrentWorkspace();
    const { groupAbility } = PermissionService();
    const canUpdateGroup = groupAbility(props, workspace).can("update", "Group");

    const availableGroups = groups.filter((group) => ![...childrenGroupIds, props.id].includes(group.id));
    const noParentGroupValue = 0;
    const options = getNestedGroupsWithIndentation(availableGroups);

    const handleUpdateName = async (name: string) => {
        const errors = validateGroupForm(
            {
                name,
                description: props.description || "",
                parentId: props.parentId ?? null,
            },
            groups,
            groupNestingLimit,
            props.id,
        );

        if (errors.name) {
            showErrorToast(errors.name);
            return;
        }

        await dispatch(updateGroup({ id: props.id, name }))
            .unwrap()
            .then(() => {
                showSuccessToast(t("sidepanels:groupPanel.toast.updateGroupSuccess"));
            })
            .catch((_error) => {
                throw new Error(t("sidepanels:groupPanel.toast.updateGroupError"));
            });
    };

    const handleUpdateDescription = async (description: string) => {
        await dispatch(updateGroup({ id: props.id, description }))
            .unwrap()
            .then(() => {
                showSuccessToast(t("sidepanels:groupPanel.toast.updateGroupSuccess"));
            })
            .catch((_error) => {
                throw new Error(t("sidepanels:groupPanel.toast.updateGroupError"));
            });
    };

    const handleUpdateParentGroup = async (parentId: string | number | null) => {
        let parsedParentId = parentId;

        if (typeof parsedParentId === "string") {
            return;
        }

        if (parsedParentId === noParentGroupValue) {
            parsedParentId = null;
        }

        const errors = validateGroupForm(
            {
                name: props.name,
                description: props.description || "",
                parentId: parsedParentId,
            },
            groups,
            groupNestingLimit,
            props.id,
        );

        if (errors.parentId) {
            showErrorToast(errors.parentId);
            return;
        }

        await dispatch(updateGroupParent({ id: props.id, parentId: parsedParentId }))
            .unwrap()
            .then(() => {
                showSuccessToast(t("sidepanels:groupPanel.toast.updateGroupSuccess"));
            })
            .catch((_error) => {
                throw new Error(t("sidepanels:groupPanel.toast.updateGroupError"));
            });
    };

    const showDevicesFilteredBy = (
        extraFilter: { id: string; value: string[] },
        ...columnsToShow: ("groupName" | "licenseStatus" | "onlineStatus")[]
    ) => {
        const filters = [{ id: "groupName", value: [props.id.toString()] }, extraFilter];
        UserSettingsService.setWorkspaceValue("devicePage.columnFilters", filters);
        columnsToShow.forEach(setColumnVisibility);
        dispatch(updateSearchQuery(""));
        if (isOnDevicePage) {
            dispatch(applyDevicePageFiltersExternally());
            dispatch(closeDetails());
        } else {
            navigate("/dashboard/devices");
        }
    };

    const handleUsedLicenseClick = () =>
        showDevicesFilteredBy({ id: "licenseStatus", value: ["Used"] }, "groupName", "licenseStatus");

    const handleUnusedLicenseClick = () =>
        showDevicesFilteredBy({ id: "licenseStatus", value: ["Unused"] }, "groupName", "licenseStatus");

    const handleOnlineClick = () => showDevicesFilteredBy({ id: "onlineStatus", value: ["Online"] }, "groupName", "onlineStatus");

    const handleOfflineClick = () =>
        showDevicesFilteredBy({ id: "onlineStatus", value: ["Offline"] }, "groupName", "onlineStatus");

    return (
        <PaneElement.Container>
            <PaneElement.Header>{t("common:label.general")}</PaneElement.Header>
            <PropertyRow>
                <PropertyKey>{t("common:label.id")}</PropertyKey>
                <PropertyValue>{props.id}</PropertyValue>
            </PropertyRow>

            <PaneElement.Section>
                <PropertyRow>
                    <PropertyKey>{t("sidepanels:groupPanel.overviewTab.name")}</PropertyKey>
                    <PropertyValue>
                        <PaneElement.EditableInput
                            initialValue={props.name}
                            onSave={handleUpdateName}
                            disabledTooltip={
                                !canUpdateGroup ? t("sidepanels:groupPanel.tooltip.noPermissionToEditGroup") : undefined
                            }
                        />
                    </PropertyValue>
                </PropertyRow>

                <PropertyRow>
                    <PropertyKey>{t("common:label.description")}</PropertyKey>
                    <PropertyValue>
                        <PaneElement.EditableInput
                            initialValue={props.description || "-"}
                            onSave={handleUpdateDescription}
                            disabledTooltip={
                                !canUpdateGroup ? t("sidepanels:groupPanel.tooltip.noPermissionToEditGroup") : undefined
                            }
                        />
                    </PropertyValue>
                </PropertyRow>

                <PropertyRow>
                    <PropertyKey>{t("sidepanels:groupPanel.overviewTab.parentGroup.caption")}</PropertyKey>
                    <PropertyValue>
                        <PaneElement.EditableSelectInput
                            label={
                                parentGroup ? (
                                    <TextLink
                                        onClick={() => {
                                            dispatch(showDetails({ type: "group", selectedId: parentGroup.id }));
                                        }}
                                        tooltip={t("common:tooltip.showGroupDetails")}
                                        truncatedTooltip={parentGroup.name}
                                    >
                                        {parentGroup.name}
                                    </TextLink>
                                ) : (
                                    "-"
                                )
                            }
                            editAction="move"
                            initialValue={props.parentId || noParentGroupValue}
                            onSave={handleUpdateParentGroup}
                            options={options}
                            disabledTooltip={
                                !canUpdateGroup ? t("sidepanels:groupPanel.tooltip.noPermissionToEditGroup") : undefined
                            }
                            disableClearable={false}
                            disableSorting
                        />
                    </PropertyValue>
                </PropertyRow>
            </PaneElement.Section>

            <PaneElement.Section>
                <PaneElement.Header>{t("sidepanels:groupPanel.overviewTab.licenses")}</PaneElement.Header>
                <PropertyRow>
                    <PropertyKey>{t("common:status.used")}</PropertyKey>
                    <PropertyValue>
                        {activeLicenseCount === 0 ? (
                            activeLicenseCount
                        ) : (
                            <TextLink
                                onClick={handleUsedLicenseClick}
                                tooltip={t("common:tooltip.showDevices", { count: activeLicenseCount })}
                            >
                                {activeLicenseCount}
                            </TextLink>
                        )}
                    </PropertyValue>
                </PropertyRow>
                <PropertyRow>
                    <PropertyKey>{t("common:status.unused")}</PropertyKey>
                    <PropertyValue>
                        {unusedLicenseCount === 0 ? (
                            unusedLicenseCount
                        ) : (
                            <TextLink
                                onClick={handleUnusedLicenseClick}
                                tooltip={t("common:tooltip.showDevices", { count: unusedLicenseCount })}
                            >
                                {unusedLicenseCount}
                            </TextLink>
                        )}
                    </PropertyValue>
                </PropertyRow>
            </PaneElement.Section>

            <PaneElement.Section withoutBorder>
                <PaneElement.Header>{t("sidepanels:groupPanel.overviewTab.devices")}</PaneElement.Header>
                <PropertyRow>
                    <PropertyKey>{t("common:status.online")}</PropertyKey>
                    <PropertyValue>
                        {onlineDeviceCount === 0 ? (
                            onlineDeviceCount
                        ) : (
                            <TextLink
                                onClick={handleOnlineClick}
                                tooltip={t("common:tooltip.showDevices", { count: onlineDeviceCount })}
                            >
                                {onlineDeviceCount}
                            </TextLink>
                        )}
                    </PropertyValue>
                </PropertyRow>
                <PropertyRow>
                    <PropertyKey>{t("sidepanels:groupPanel.overviewTab.offline")}</PropertyKey>
                    <PropertyValue>
                        {offlineDeviceCount === 0 ? (
                            offlineDeviceCount
                        ) : (
                            <TextLink
                                onClick={handleOfflineClick}
                                tooltip={t("common:tooltip.showDevices", { count: offlineDeviceCount })}
                            >
                                {offlineDeviceCount}
                            </TextLink>
                        )}
                    </PropertyValue>
                </PropertyRow>
            </PaneElement.Section>
        </PaneElement.Container>
    );
};

export default OverviewTab;
